// apps/admin-ui/src/components/UserSettings.tsx
// User settings dropdown with theme selection
// ALL RIGHTS RESERVED

import { useState, useRef, useEffect } from 'react';
import { useTheme } from '../hooks/useTheme';

interface UserSettingsProps {
  employeeName: string;
  employeeRoles: string;
  onLogout: () => void;
}

const SunIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

const MoonIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const MonitorIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

export function UserSettings({ employeeName, employeeRoles, onLogout }: UserSettingsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { theme, setTheme, resolvedTheme } = useTheme();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const themeOptions = [
    { value: 'light' as const, label: 'Light', icon: <SunIcon /> },
    { value: 'dark' as const, label: 'Dark', icon: <MoonIcon /> },
    { value: 'system' as const, label: 'System', icon: <MonitorIcon /> },
  ];

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 p-2 rounded-lg hover:bg-surface-muted transition-colors"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="text-right">
          <div className="text-sm font-medium text-content">{employeeName}</div>
          <div className="text-2xs text-content-muted">{employeeRoles}</div>
        </div>
        <div className="w-8 h-8 rounded-full bg-brand-500 text-white flex items-center justify-center text-sm font-medium">
          {employeeName.charAt(0).toUpperCase()}
        </div>
        <svg 
          className={`w-4 h-4 text-content-muted transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div 
          className="absolute right-0 mt-2 w-64 bg-surface border border-edge rounded-lg shadow-lg z-50 overflow-hidden"
          role="menu"
        >
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-edge bg-surface-subtle">
            <div className="font-medium text-content">{employeeName}</div>
            <div className="text-sm text-content-muted">{employeeRoles}</div>
          </div>

          {/* Theme Selection */}
          <div className="p-3">
            <div className="px-1 pb-2 text-xs font-medium text-content-muted uppercase tracking-wider">
              Theme
            </div>
            <div className="space-y-1">
              {themeOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setTheme(option.value)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm
                    transition-colors text-left
                    ${theme === option.value 
                      ? 'bg-brand-500/10 text-brand-500' 
                      : 'hover:bg-surface-muted text-content'
                    }
                  `}
                  role="menuitem"
                >
                  <span className="opacity-70">{option.icon}</span>
                  <span className="flex-1">{option.label}</span>
                  {theme === option.value && (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
            {theme === 'system' && (
              <div className="px-3 py-1.5 text-xs text-content-muted">
                Currently using {resolvedTheme} mode
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-edge" />

          {/* Sign Out */}
          <div className="p-2">
            <button
              onClick={() => {
                setIsOpen(false);
                onLogout();
              }}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-error hover:bg-error-subtle transition-colors"
              role="menuitem"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span>Sign out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserSettings;
